import React, { useEffect } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Home, BarChart3, ShieldCheck, UsersRound } from "lucide-react";
import { Nav } from "./components/Nav";
import { CompanionOverlay } from "./components/CompanionOverlay";
import { RigIntelligenceSurface } from "./components/RigIntelligenceSurface";
import { RouteScrollToTop } from "./components/RouteScrollToTop";
import { SessionLifecycleSurface } from "./components/SessionLifecycleSurface";
import HomeSimplePage from "./pages/HomeSimple";
import LakesPage from "./pages/Lakes";
import StatsWithHistoryPage from "./pages/StatsWithHistory";
import SharedPage from "./pages/Shared";
import StartSessionSimplePage from "./pages/StartSessionSimple";
import SessionEntryPage from "./pages/SessionEntry";
import SessionSetupPage from "./pages/SessionSetup";
import AutoEndReviewPage from "./pages/AutoEndReview";
import LogCatchPage from "./pages/LogCatch";
import LakeDetailsPage from "./pages/LakeDetails";
import LakeMapPage from "./pages/LakeMap";
import SessionReplayPage from "./pages/SessionReplay";
import SessionReplayV2Page from "./pages/SessionReplayV2";
import CatchDetailsPage from "./pages/CatchDetails";
import AdvisorPage from "./pages/Advisor";
import AdvisorResultsPage from "./pages/AdvisorResults";
import AdvisorBothResultsPage from "./pages/AdvisorBothResults";
import FindLakePage from "./pages/FindLake";
import MembershipsPage from "./pages/Memberships";
import AccountPage from "./pages/Account";
import AdminPage from "./pages/Admin";
import AdminLakeReviewsPage from "./pages/AdminLakeReviews";
import AdminLakeReviewDetailPage from "./pages/AdminLakeReviewDetail";
import AdminCanonicalLakeEditPage from "./pages/AdminCanonicalLakeEdit";
import AdminAccountsPage from "./pages/AdminAccounts";
import AdminSwimReviewsPage from "./pages/AdminSwimReviews";
import AdminBaitReviewsPage from "./pages/AdminBaitReviews";
import AdminTackleLibraryPage from "./pages/AdminTackleLibrary";
import ScoutFixedLakePage from "./pages/ScoutFixedLake";
import StartFishingPage from "./pages/StartFishing";
import {
  cacheOfflineLakes,
  cacheServerSessions,
  syncAllOfflineSessions,
} from "./lib/offline-session";

const baseNavItems = [
  { href: "/app", label: "Home", icon: <Home /> },
  { href: "/community", label: "Community", icon: <UsersRound /> },
  { href: "/stats", label: "Stats", icon: <BarChart3 /> },
];

function useAdminStatus() {
  return useQuery({
    queryKey: ["admin_me"],
    queryFn: () => fetch("/app-api/admin/me", { credentials: "include", cache: "no-store" }).then((r) => (r.ok ? r.json() : { isAdmin: false })).catch(() => ({ isAdmin: false })),
    staleTime: 60_000,
  });
}

function AdminOnly({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useAdminStatus();
  if (isLoading) return <div className="mx-auto max-w-[1200px] px-4 py-8 text-sm text-secondary">Checking admin access…</div>;
  if (data?.isAdmin !== true) return <div className="mx-auto max-w-[1200px] px-4 py-8"><div className="rounded-lg border border-border bg-raised p-6 text-sm text-secondary">Admin access only.</div></div>;
  return <>{children}</>;
}

function SessionRigIntelligenceSurface() {
  const { pathname } = useLocation();
  if (!/^\/(?:sessions|live)\/\d+/.test(pathname)) return null;
  return <RigIntelligenceSurface />;
}

function AdvisorEntry() {
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get("sessionId");

  if (!sessionId) return <AdvisorPage />;

  return (
    <div className="mx-auto max-w-[760px] space-y-4 px-4 py-6 pb-28">
      <Link to={`/live/${sessionId}`} className="inline-flex min-h-10 items-center text-sm font-medium text-accent">← Back to live session</Link>
      <section className="rounded-xl border border-border bg-raised p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">Live session</p>
        <h1 className="mt-1 text-xl font-semibold text-primary">Ask CatchCompass</h1>
        <p className="mt-2 text-sm leading-6 text-secondary">Talk through what is happening on the bank. CatchCompass uses this live session as the context for the conversation.</p>
      </section>
      <CompanionOverlay
        context={{ type: "session", sessionId }}
        externalQuestion={{ id: `open-live-session-${sessionId}`, text: "" }}
      />
    </div>
  );
}

export default function App() {
  const { data: adminData } = useAdminStatus();
  const queryClient = useQueryClient();

  useEffect(() => {
    let stopped = false;
    const refreshOfflineReadyData = async () => {
      if (!navigator.onLine || stopped) return;
      try {
        const [lakesResponse, sessionsResponse] = await Promise.all([fetch("/app-api/lakes"), fetch("/app-api/sessions")]);
        if (lakesResponse.ok) cacheOfflineLakes(await lakesResponse.json());
        if (sessionsResponse.ok) cacheServerSessions(await sessionsResponse.json());
        const synced = await syncAllOfflineSessions();
        if (synced.length && !stopped) {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["carpintel_sessions"] }),
            queryClient.invalidateQueries({ queryKey: ["carpintel_catches"] }),
            queryClient.invalidateQueries({ queryKey: ["carpintel_stats"] }),
            queryClient.invalidateQueries({ queryKey: ["catchcompass_rig_intelligence"] }),
            queryClient.invalidateQueries({ queryKey: ["catchcompass_tackle_intelligence"] }),
          ]);
        }
      } catch {
        // Patchy bankside connectivity must never risk local session data.
      }
    };
    void refreshOfflineReadyData();
    window.addEventListener("online", refreshOfflineReadyData);
    return () => { stopped = true; window.removeEventListener("online", refreshOfflineReadyData); };
  }, [queryClient]);

  const navItems = adminData?.isAdmin === true
    ? [...baseNavItems, { href: "/admin", label: "Admin", icon: <ShieldCheck /> }]
    : baseNavItems;

  return (
    <BrowserRouter>
      <RouteScrollToTop />
      <Nav items={navItems} />
      <SessionLifecycleSurface />
      <SessionRigIntelligenceSurface />
      <main className="min-h-dvh bg-background text-primary">
        <Routes>
          <Route path="/app" element={<HomeSimplePage />} />
          <Route path="/lakes" element={<Navigate to="/scout" replace />} />
          <Route path="/lakes/find" element={<FindLakePage />} />
          <Route path="/lakes/memberships" element={<MembershipsPage />} />
          <Route path="/lakes/:id/map" element={<LakeMapPage />} />
          <Route path="/lakes/:id" element={<LakeDetailsPage />} />
          <Route path="/sessions" element={<Navigate to="/stats" replace />} />
          <Route path="/sessions/new" element={<StartSessionSimplePage />} />
          <Route path="/live/:id" element={<SessionEntryPage />} />
          <Route path="/sessions/:id/setup" element={<SessionSetupPage />} />
          <Route path="/sessions/:id/review-auto-end" element={<AutoEndReviewPage />} />
          <Route path="/sessions/:id/replay" element={<SessionReplayV2Page />} />
          <Route path="/sessions/:id/replay-map" element={<SessionReplayPage />} />
          <Route path="/sessions/:id" element={<SessionEntryPage />} />
          <Route path="/catches/new" element={<LogCatchPage />} />
          <Route path="/catches/:id" element={<CatchDetailsPage />} />
          <Route path="/community" element={<SharedPage />} />
          <Route path="/shared" element={<Navigate to="/community" replace />} />
          <Route path="/stats" element={<StatsWithHistoryPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/advisor" element={<AdvisorEntry />} />
          <Route path="/advisor/both" element={<AdvisorBothResultsPage />} />
          <Route path="/advisor/:id" element={<AdvisorResultsPage />} />
          <Route path="/scout" element={<StartFishingPage />} />
          <Route path="/beta/les-quis-scout" element={<ScoutFixedLakePage />} />
          <Route path="/admin" element={<AdminOnly><AdminPage /></AdminOnly>} />
          <Route path="/admin/lakes" element={<AdminOnly><LakesPage /></AdminOnly>} />
          <Route path="/admin/lake-reviews" element={<AdminOnly><AdminLakeReviewsPage /></AdminOnly>} />
          <Route path="/admin/bait-reviews" element={<AdminOnly><AdminBaitReviewsPage /></AdminOnly>} />
          <Route path="/admin/tackle" element={<AdminOnly><AdminTackleLibraryPage /></AdminOnly>} />
          <Route path="/admin/accounts" element={<AdminOnly><AdminAccountsPage /></AdminOnly>} />
          <Route path="/admin/swim-reviews" element={<AdminOnly><AdminSwimReviewsPage /></AdminOnly>} />
          <Route path="/admin/lake-reviews/:id" element={<AdminOnly><AdminLakeReviewDetailPage /></AdminOnly>} />
          <Route path="/admin/canonical-lakes/:id/edit" element={<AdminOnly><AdminCanonicalLakeEditPage /></AdminOnly>} />
        </Routes>
      </main>
    </BrowserRouter>
  );
}
